import findLast from 'lodash/findLast';
import isNil from 'lodash/isNil';
import { type MouseEvent, type ReactNode, useEffect, useRef, useState } from 'react';
import {
  Bar,
  type BarShapeProps,
  CartesianGrid,
  BarChart as RechartsBarChart,
  type MouseHandlerDataParam,
  Rectangle,
  Text,
  Tooltip,
  type TooltipPayloadEntry,
  type TooltipValueType,
  XAxis,
  type XAxisTickContentProps,
  YAxis,
} from 'recharts';

import { Button, ButtonVariant } from '@ui/components/base/button';
import { ChartContainer, ChartTooltipContent } from '@ui/components/base/chart';
import { Div } from '@ui/components/base/div';
import { getBarIndexAtX } from '@ui/components/charts/barChartUtils';
import { type DrillAnchor, pointDrillAnchor } from '@ui/components/popovers/DrillPopover';
import { Typography, TypographyVariant } from '@ui/components/texts/Typography';
import { cn } from '@ui/lib/utils';

const BAR_RADIUS: [number, number, number, number] = [4, 4, 0, 0];

const FLAT_RADIUS = 0;

const STACK_ID = 'bars';

const MIN_VISIBLE_BAR_HEIGHT = 3;

const getMinBarHeight = (value: number | undefined | null): number =>
  !isNil(value) && value > 0 ? MIN_VISIBLE_BAR_HEIGHT : 0;

const CHART_MARGIN = { top: 8, right: 8, bottom: 20, left: 16 };

const AXIS_TICK = { fontSize: 11, fill: 'var(--fg-muted)' };

const EMPHASIZED_TICK_FILL = 'var(--primary)';

const EMPHASIZED_TICK_WEIGHT = 700;

const AXIS_LABEL_STYLE = { fontSize: 11, fontWeight: 600, fill: 'var(--fg-muted)' };

const Y_AXIS_WIDTH = 56;

const X_AXIS_HEIGHT = 30;

const PLOT_AREA_INSET = {
  top: CHART_MARGIN.top,
  right: CHART_MARGIN.right,
  bottom: CHART_MARGIN.bottom + X_AXIS_HEIGHT,
  left: CHART_MARGIN.left + Y_AXIS_WIDTH,
};

const TOOLTIP_CURSOR = { fill: 'var(--border)', opacity: 0.5 };

const DIMMED_BAR_OPACITY = 0.3;

const DEFAULT_SERIES_COLOR = 'var(--primary)';

interface BarChartSeries {
  key: string;
  label: string;
  color?: string;
}

interface BarChartPoint {
  label: string;
  tooltipLabel?: string;
  values: Record<string, number>;
}

interface BarChartProps {
  points: BarChartPoint[];
  series: BarChartSeries[];
  chartLabel: string;
  xAxisLabel: string;
  yAxisLabel: string;
  totalLabel?: string;
  highlightedIndex?: number | null;
  emphasizedIndex?: number | null;
  formatValue?: (value: number) => string;
  isBarInteractive?: (point: BarChartPoint) => boolean;
  onBarClick?: (index: number, anchor: DrillAnchor) => void;
  onBarHover?: (index: number) => void;
  className?: string;
}

const getPointValue = (point: BarChartPoint, key: string): number => point.values[key] ?? 0;

const getSegmentMinHeight =
  (points: BarChartPoint[], key: string) =>
  (_value: number | undefined | null, index: number): number => {
    const point = points[index];
    return isNil(point) ? 0 : getMinBarHeight(getPointValue(point, key));
  };

const getPointTotal = (point: BarChartPoint, series: BarChartSeries[]): number =>
  series.reduce((total, { key }) => total + getPointValue(point, key), 0);

const hasValue = (point: BarChartPoint, series: BarChartSeries[]): boolean => getPointTotal(point, series) > 0;

const formatAmount = (value: number, formatValue?: (value: number) => string): string =>
  isNil(formatValue) ? String(value) : formatValue(value);

const formatTooltipLabel = (label: ReactNode, payload: readonly TooltipPayloadEntry[]): ReactNode =>
  payload[0]?.payload?.tooltipLabel ?? label;

interface StackSegmentProps extends Partial<BarShapeProps> {
  seriesKey: string;
  topSeriesKeys: (string | undefined)[];
  highlightedIndex: number | null;
}

const StackSegment = ({ seriesKey, topSeriesKeys, highlightedIndex, ...props }: StackSegmentProps) => {
  const index = props.originalDataIndex ?? -1;
  return (
    <Rectangle
      {...props}
      radius={topSeriesKeys[index] === seriesKey ? BAR_RADIUS : FLAT_RADIUS}
      fillOpacity={isNil(highlightedIndex) || index === highlightedIndex ? 1 : DIMMED_BAR_OPACITY}
    />
  );
};

interface EmphasizedTickProps extends Partial<XAxisTickContentProps> {
  emphasizedIndex: number;
}

const EmphasizedTick = ({ emphasizedIndex, x, y, index, payload, textAnchor, verticalAnchor }: EmphasizedTickProps) => {
  const emphasized = index === emphasizedIndex;
  return (
    <Text
      x={x}
      y={y}
      textAnchor={textAnchor}
      verticalAnchor={verticalAnchor}
      fontSize={AXIS_TICK.fontSize}
      fill={emphasized ? EMPHASIZED_TICK_FILL : AXIS_TICK.fill}
      fontWeight={emphasized ? EMPHASIZED_TICK_WEIGHT : undefined}
    >
      {payload?.value}
    </Text>
  );
};

interface BarChartKeyboardTargetsProps {
  points: BarChartPoint[];
  series: BarChartSeries[];
  formatValue?: (value: number) => string;
  isBarInteractive: (point: BarChartPoint) => boolean;
  onBarClick: (index: number, anchor: DrillAnchor) => void;
  onBarHover?: (index: number) => void;
}

const getKeyboardTargetLabel = (
  point: BarChartPoint,
  series: BarChartSeries[],
  formatValue?: (value: number) => string
): string => {
  const title = point.tooltipLabel ?? point.label;
  if (series.length === 1) {
    return `${title}: ${formatAmount(getPointValue(point, series[0].key), formatValue)}`;
  }
  const values = series.map(({ key, label }) => `${label} ${formatAmount(getPointValue(point, key), formatValue)}`);
  return `${title}: ${values.join(', ')}`;
};

const BarChartKeyboardTargets = ({
  points,
  series,
  formatValue,
  isBarInteractive,
  onBarClick,
  onBarHover,
}: BarChartKeyboardTargetsProps) => (
  <Div row className="pointer-events-none absolute" style={PLOT_AREA_INSET}>
    {points.map((point, index) =>
      isBarInteractive(point) ? (
        <Button
          key={point.tooltipLabel ?? point.label}
          variant={ButtonVariant.GHOST}
          className="h-auto flex-1 rounded-sm border-0 p-0"
          aria-label={getKeyboardTargetLabel(point, series, formatValue)}
          onClick={(event) => {
            const target = event.currentTarget;
            const bounds = target.getBoundingClientRect();
            onBarClick(index, pointDrillAnchor(target, bounds.left + bounds.width / 2, bounds.bottom));
          }}
          onFocus={() => onBarHover?.(index)}
        />
      ) : (
        <Div key={point.tooltipLabel ?? point.label} className="flex-1" />
      )
    )}
  </Div>
);

const BarChart = ({
  points,
  series,
  chartLabel,
  xAxisLabel,
  yAxisLabel,
  totalLabel,
  highlightedIndex = null,
  emphasizedIndex = null,
  formatValue,
  isBarInteractive,
  onBarClick,
  onBarHover,
  className,
}: BarChartProps) => {
  const [tooltipSuspended, setTooltipSuspended] = useState<boolean>(false);
  const chartAreaRef = useRef<HTMLDivElement>(null);
  const hoveredIndexRef = useRef<number | null>(null);
  const isInteractive = isBarInteractive ?? ((point: BarChartPoint) => hasValue(point, series));
  const seriesKeys = series.map(({ key }) => key);
  const topSeriesKeys = points.map((point) => findLast(seriesKeys, (key) => getPointValue(point, key) > 0));
  const tickFormatter = isNil(formatValue) ? undefined : (value: number) => formatValue(value);
  const isStacked = series.length > 1;

  const formatTooltipValue = (value: TooltipValueType | undefined, name: string | number | undefined): ReactNode => {
    const amount = formatAmount(Number(value), formatValue);
    if (!isStacked) {
      return amount;
    }
    const color = series.find(({ label }) => label === name)?.color ?? DEFAULT_SERIES_COLOR;
    return (
      <>
        <Div className="size-2.5 shrink-0 rounded-xs" style={{ backgroundColor: color }} />
        <Typography variant={TypographyVariant.MUTED} size="xs" className="font-medium">
          {name}
        </Typography>
        <Typography size="xs" className="ml-auto">
          {amount}
        </Typography>
      </>
    );
  };

  const renderTooltipTotal = (payload: readonly TooltipPayloadEntry[]): ReactNode => {
    if (!isStacked || isNil(totalLabel)) {
      return null;
    }
    const total = payload.reduce((sum, item) => sum + Number(item.value ?? 0), 0);
    return (
      <Div row className="w-full items-center gap-2 border-t border-border pt-1.5">
        <Typography size="xs">{totalLabel}</Typography>
        <Typography size="xs" className="ml-auto">
          {formatAmount(total, formatValue)}
        </Typography>
      </Div>
    );
  };

  const getInteractiveIndex = ({ activeTooltipIndex }: MouseHandlerDataParam): number | null => {
    if (isNil(activeTooltipIndex)) {
      return null;
    }
    const index = Number(activeTooltipIndex);
    const point = points[index];
    return !isNil(point) && isInteractive(point) ? index : null;
  };

  const handleChartMouseMove = (state: MouseHandlerDataParam) => {
    setTooltipSuspended(false);
    const index = getInteractiveIndex(state);
    if (isNil(onBarHover) || isNil(index) || hoveredIndexRef.current === index) {
      return;
    }
    hoveredIndexRef.current = index;
    onBarHover(index);
  };

  const handleChartClick = (_state: MouseHandlerDataParam, event: MouseEvent<SVGGraphicsElement>) => {
    const chartArea = chartAreaRef.current;
    if (isNil(onBarClick) || isNil(chartArea)) {
      return;
    }
    const { left, right } = chartArea.getBoundingClientRect();
    const index = getBarIndexAtX(
      event.clientX,
      left + PLOT_AREA_INSET.left,
      right - PLOT_AREA_INSET.right,
      points.length
    );
    const point = isNil(index) ? undefined : points[index];
    if (!isNil(index) && !isNil(point) && isInteractive(point)) {
      onBarClick(index, pointDrillAnchor(chartArea, event.clientX, event.clientY));
    }
  };

  useEffect(() => {
    const suspendTooltip = () => setTooltipSuspended(true);
    window.addEventListener('scroll', suspendTooltip, { capture: true, passive: true });
    return () => window.removeEventListener('scroll', suspendTooltip, { capture: true });
  }, []);

  return (
    <Div
      ref={chartAreaRef}
      className={cn('relative size-full min-h-0', className)}
      onFocus={() => setTooltipSuspended(false)}
      onKeyDown={() => setTooltipSuspended(false)}
    >
      <ChartContainer role="img" aria-label={chartLabel} className="aspect-auto size-full min-h-0">
        <RechartsBarChart
          data={points}
          margin={CHART_MARGIN}
          accessibilityLayer={isNil(onBarClick)}
          className={isNil(onBarClick) ? undefined : 'cursor-pointer'}
          onClick={isNil(onBarClick) ? undefined : handleChartClick}
          onMouseMove={handleChartMouseMove}
          onMouseLeave={() => {
            hoveredIndexRef.current = null;
          }}
        >
          <CartesianGrid vertical={false} stroke="var(--border)" />
          <XAxis
            dataKey="label"
            height={X_AXIS_HEIGHT}
            tickLine={false}
            axisLine={false}
            tick={isNil(emphasizedIndex) ? AXIS_TICK : <EmphasizedTick emphasizedIndex={emphasizedIndex} />}
            interval={0}
            label={{ value: xAxisLabel, position: 'insideBottom', offset: -14, style: AXIS_LABEL_STYLE }}
          />
          <YAxis
            width={Y_AXIS_WIDTH}
            tickLine={false}
            axisLine={false}
            tick={AXIS_TICK}
            tickFormatter={tickFormatter}
            label={{ value: yAxisLabel, angle: -90, position: 'insideLeft', offset: -8, style: AXIS_LABEL_STYLE }}
          />
          <Tooltip
            active={tooltipSuspended ? false : undefined}
            cursor={TOOLTIP_CURSOR}
            content={
              <ChartTooltipContent
                className="font-bold"
                labelFormatter={formatTooltipLabel}
                formatter={formatTooltipValue}
                renderFooter={renderTooltipTotal}
              />
            }
          />
          {series.map(({ key, label, color = DEFAULT_SERIES_COLOR }) => (
            <Bar
              key={key}
              dataKey={(point: BarChartPoint) => getPointValue(point, key)}
              name={label}
              stackId={isStacked ? STACK_ID : undefined}
              fill={color}
              minPointSize={getSegmentMinHeight(points, key)}
              isAnimationActive={false}
              shape={<StackSegment seriesKey={key} topSeriesKeys={topSeriesKeys} highlightedIndex={highlightedIndex} />}
            />
          ))}
        </RechartsBarChart>
      </ChartContainer>
      {!isNil(onBarClick) && (
        <BarChartKeyboardTargets
          points={points}
          series={series}
          formatValue={formatValue}
          isBarInteractive={isInteractive}
          onBarClick={onBarClick}
          onBarHover={onBarHover}
        />
      )}
    </Div>
  );
};

export { BarChart };
export type { BarChartPoint, BarChartProps, BarChartSeries };
